import { useEffect, lazy, Suspense } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Hero from "@/pages/home/components/Hero";
import SEO from "@/components/SEO";
import { SITE_URL, SITE_LOGO } from "@/config/site";

// Lazy-load all below-fold sections to reduce initial bundle and render-blocking
const ClientLogoStrip    = lazy(() => import("@/pages/home/components/ClientLogoStrip"));
const TrustBar           = lazy(() => import("@/pages/home/components/TrustBar"));
const SDAVExplained      = lazy(() => import("@/pages/home/components/SDAVExplained"));
const PlatformFeatures   = lazy(() => import("@/pages/home/components/PlatformFeatures"));
const CaseStudies        = lazy(() => import("@/pages/home/components/CaseStudies"));
const ClientTestimonials = lazy(() => import("@/pages/home/components/ClientTestimonials"));
const KeyMetrics         = lazy(() => import("@/pages/home/components/KeyMetrics"));
const ContactSection     = lazy(() => import("@/pages/home/components/ContactSection"));
const FooterSection      = lazy(() => import("@/pages/home/components/FooterSection"));

// Minimal skeleton placeholder while lazy chunks load
function SectionSkeleton() {
  return <div className="w-full py-16 bg-white" aria-hidden="true" />;
}

export default function Home() {
  // Scroll to hash anchor on mount (for cross-page navigation)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const tryScroll = () => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      };
      // Delay to ensure DOM is fully rendered
      const t = setTimeout(tryScroll, 120);
      // Also try once more after a longer delay for images/layout
      const t2 = setTimeout(tryScroll, 600);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
  }, []);

  const canonicalUrl = `${SITE_URL}/`;

  // Each object renders as its own <script type="application/ld+json"> block
  const schema = [
    // Block 1 — Organisation
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "CUBE MC",
      "url": SITE_URL,
      "logo": SITE_LOGO,
      "description": "Software-Defined AV platform for multi-site enterprises. CUBE MC manages 3,000+ sites globally.",
      "sameAs": ["https://www.linkedin.com/company/cube-mc"],
      "address": [
        {
          "@type": "PostalAddress",
          "streetAddress": "Suite 340, 600 Crowfoot Crescent NW",
          "addressLocality": "Calgary",
          "addressRegion": "Alberta",
          "postalCode": "T3G 0B4",
          "addressCountry": "CA",
        },
        {
          "@type": "PostalAddress",
          "streetAddress": "435 Merchant Walk Square, Ste 300-617",
          "addressLocality": "Charlottesville",
          "addressRegion": "Virginia",
          "postalCode": "22902",
          "addressCountry": "US",
        },
      ],
      "contactPoint": [
        {"@type": "ContactPoint", "telephone": "+1-403-800-3333", "contactType": "sales", "areaServed": "CA"},
        {"@type": "ContactPoint", "telephone": "+1-855-698-2333", "contactType": "sales", "areaServed": "US"},
      ],
    },

    // Block 2 — Software
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "CUBE OS",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "IP Network Infrastructure",
      "description": "CUBE OS is the proprietary operating system of the CUBE MC SDAV platform, managing AV delivery and behavioral content triggering across 3,000+ enterprise sites.",
      "offers": {"@type": "Offer", "seller": {"@type": "Organization", "name": "CUBE MC"}},
    },

    // Block 3 — WebPage with SpeakableSpecification
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/#webpage`,
      "url": `${SITE_URL}/`,
      "name": "Software-Defined AV Platform — Reconfigure Experience, Not Infrastructure | CUBE MC",
      "description": "CUBE MC is a Software-Defined AV platform for multi-site enterprises. Manage every audio and visual environment from one interface — no rewiring, no vendor visits, full behavioral control.",
      "inLanguage": "en",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "about": { "@id": `${SITE_URL}/#org` },
      "datePublished": "2024-01-01",
      "dateModified": "2026-04-29",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [
          "h1",
          "[data-speakable='hero-description']",
          "[data-speakable='value-prop']",
        ],
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${SITE_URL}/`,
          },
        ],
      },
    },

    // Block 4 — WebSite (enables Sitelinks Searchbox)
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "url": `${SITE_URL}/`,
      "name": "CUBE MC",
      "description": "Software-Defined AV platform for multi-site enterprises.",
      "publisher": { "@id": `${SITE_URL}/#org` },
      "inLanguage": "en",
    },

    // Block 5 — FAQPage (SDAV Glossary — 14 terms)
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Software-Defined AV (SDAV)?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Software-Defined AV decouples the audio and visual experience from the physical infrastructure on which it runs. The entire AV environment — zones, content, schedules, and behaviors — is controlled through software, meaning any aspect of the experience can be changed instantly without physical intervention. Organizations can manage thousands of sites from a single interface without rewiring, site visits, or hardware changes.",
          },
        },
        {
          "@type": "Question",
          "name": "What is AV-over-IP?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "AV-over-IP routes audio and video signals over standard IP networks rather than dedicated AV cabling. Because AV signals travel over the same infrastructure as data, organizations can manage, reconfigure, and scale their AV environments using network tools — eliminating specialist cabling and enabling remote control across multiple locations from a single platform.",
          },
        },
        {
          "@type": "Question",
          "name": "What is CUBE OS?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CUBE OS is the resilient operating layer that powers local orchestration devices used with CUBE MC deployments. It stores all behavioral programs, schedules, and content locally on-device — meaning environments continue to perform, trigger, and respond even during complete internet outages. CUBE OS can deliver a guaranteed uptime SLA and is the technical foundation of CUBE MC's network-resilient architecture.",
          },
        },
        {
          "@type": "Question",
          "name": "What is Behavioral AV?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Behavioral AV programs environmental experiences to respond to real-world triggers — time of day, occupancy, foot traffic, transaction activity, or external data inputs. Rather than playing static content on a fixed schedule, a behavioral AV system adapts the environment dynamically to influence customer behavior, dwell time, and purchasing decisions. Behavioral AV is the defining characteristic that separates Software-Defined AV from traditional broadcast AV systems.",
          },
        },
        {
          "@type": "Question",
          "name": "What is an AV Zone?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "An AV zone is a defined physical or logical area within a building or site that receives independently controlled audio and visual content. In a Software-Defined AV system, zones are created, modified, and deleted in software — meaning a retailer can instantly change the zone configuration of a 500-store estate without any physical rewiring. Zone boundaries can be adjusted in real time to match changing layouts, seasonal campaigns, or live floor events.",
          },
        },
        {
          "@type": "Question",
          "name": "What is Single Pane of Glass (SPOG) in AV management?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Single pane of glass refers to a unified management interface that consolidates control of all AV devices, zones, and sites into one platform, eliminating the need to access multiple separate tools or systems. In enterprise AV management, it means an operations team can monitor, diagnose, and reconfigure any device across thousands of locations from one interface — without logging into individual site systems or calling specialist vendors.",
          },
        },
        {
          "@type": "Question",
          "name": "What is Echoic Memory?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Echoic memory is the brain's auditory sensory buffer — the mechanism by which the brain retains and processes sound after it has been heard. Research consistently shows that echoic memory retains information with 25% greater accuracy than its visual equivalent (iconic memory). CUBE MC's behavioral audio environments are engineered to leverage echoic memory at scale — designing audio cues that influence attention, recall, and behavior below the threshold of conscious awareness.",
          },
        },
        {
          "@type": "Question",
          "name": "What is Network-Resilient AV?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Network-resilient AV describes systems designed to continue operating correctly during internet outages, network degradation, or connectivity interruptions. A network-resilient AV platform stores behavioral programs, schedules, and content locally on-device so that environments continue to function even when the connection to the central management platform is lost. This is a critical requirement for retail, hospitality, and financial services deployments where AV downtime has a direct commercial cost.",
          },
        },
        {
          "@type": "Question",
          "name": "What is Multi-Site AV Management?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Multi-site AV management is the ability to configure, monitor, and control AV environments across multiple physical locations from a centralized platform. For retail chains, hotel groups, corporate campuses, and healthcare networks, it eliminates the operational overhead of managing each location independently — enabling brand consistency, rapid rollout of changes, and centralized oversight of device health across the entire estate.",
          },
        },
        {
          "@type": "Question",
          "name": "What is AV Rewiring?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "AV rewiring refers to the physical process of changing speaker placements, screen positions, signal routing, or zone configurations by modifying the underlying cable infrastructure. In traditional fixed-architecture AV systems, any change to the experience — adding a zone, changing a speaker assignment, or repositioning a screen — requires a site visit by a specialist engineer and physical modification of the cabling. Software-Defined AV eliminates the need for rewiring by managing all configuration in software.",
          },
        },
        {
          "@type": "Question",
          "name": "What is On-Device Scheduling in AV systems?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "On-device scheduling is an AV capability in which content schedules, behavioral triggers, and operating logic are stored and executed locally on the endpoint device — rather than depending on a continuous connection to a central server. This ensures scheduled content plays correctly and zones respond to triggers regardless of network availability. On-device scheduling is the technical foundation of network-resilient AV and is built into every CUBE OS deployment.",
          },
        },
        {
          "@type": "Question",
          "name": "What is Experiential Retail AV?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Experiential retail AV is the use of audio and visual technology in physical retail environments to create immersive, branded customer experiences that influence behavior, increase dwell time, and drive purchase intent. This includes curated music programs, voice-of-brand messaging, dynamic visual content, and zone-specific soundscapes that adapt to time of day, season, or promotional campaign. Strategically managed retail AV increases purchase rates by up to 50% compared to unmanaged environments.",
          },
        },
        /*
        {
          "@type": "Question",
          "name": "What is PCI DSS Compliant AV?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "PCI DSS-compliant AV refers to audio and visual systems that meet the Payment Card Industry Data Security Standard requirements for deployment in environments that process, store, or transmit cardholder data. For retailers, financial services organizations, and hospitality businesses, AV systems operating on the same network as point-of-sale infrastructure must meet PCI DSS requirements. CUBE MC is PCI DSS Level 1 certified — the highest level — and can be deployed in payment environments without additional security configuration or network segregation.",
          },
        },
        */
        {
          "@type": "Question",
          "name": "What is Ambient Intelligence in AV?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ambient intelligence is the concept of physical environments that sense, interpret, and respond to the presence and behavior of people within them. In the context of AV systems, it enables spaces to adapt their audio, visual, and environmental characteristics in real time based on occupancy, behavioral patterns, time of day, or external triggers. Software-Defined AV is the infrastructure layer that makes ambient intelligence commercially deployable at enterprise scale.",
          },
        },
      ],
    },
  ];

  return (
    <div className="bg-white text-neutral-800 min-h-screen">
      <SEO
        title="Software-Defined AV Platform for Enterprises | CUBE MC"
        description="CUBE MC is a Software-Defined AV platform for multi-site enterprises. Manage every audio and visual environment from one interface — no rewiring, no vendor visits, full behavioral control."
        keywords="Software-Defined AV, SDAV, AV-over-IP Architecture, Behavioural AV Platform, Enterprise AV Management, CUBE MC, Multi-site AV Control, PCI DSS AV"
        canonical={canonicalUrl}
        ogTitle="Software-Defined AV Platform — Reconfigure Experience, Not Infrastructure | CUBE MC"
        ogDescription="CUBE MC is a Software-Defined AV platform for multi-site enterprises. Manage every audio and visual environment from one interface — no rewiring, no vendor visits, full behavioral control."
        ogImage={`${SITE_URL}/images/source/cube_mc_og_opt.jpg`}
        ogImageAlt="CUBE MC — Software-Defined AV Platform"
        ogImageWidth="900"
        ogImageHeight="480"
        ogImageType="image/jpeg"
        ogLocale="en_US"
        ogSeeAlso={[
          "https://www.linkedin.com/company/cube-mc",
          "https://www.cubegroup.global",
          "https://twitter.com/cubemcav",
          "https://x.com/cubemcav",
        ]}
        twitterCard="summary_large_image"
        twitterCreator="@cubemcav"
        schema={schema}
      />
      <Navbar />
      <main id="main-content">
        {/* Hero is above-fold — always eagerly loaded */}
        <Hero />
        {/* Everything below the fold is lazy-loaded */}
        <Suspense fallback={<SectionSkeleton />}>
          <ClientLogoStrip />
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <TrustBar />
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <SDAVExplained />
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <PlatformFeatures />
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <CaseStudies />
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <ClientTestimonials />
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <KeyMetrics />
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <ContactSection />
        </Suspense>
      </main>
      <Suspense fallback={<SectionSkeleton />}>
        <FooterSection />
      </Suspense>

    </div>
  );
}
